import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { 
  Phone, Building2, Bot, MessageSquare, PhoneCall, Rocket, 
  ArrowLeft, ArrowRight, Check, Play, Pause, Volume2,
  Sparkles, CheckCircle, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const WIZARD_STEPS = [
  { id: "confirm_details", title: "Confirm Details", icon: Building2 },
  { id: "choose_template", title: "Choose Template", icon: Bot },
  { id: "customize_greeting", title: "Customize Greeting", icon: MessageSquare },
  { id: "connect_phone", title: "Connect Phone", icon: Phone },
  { id: "test_call", title: "Test Call", icon: PhoneCall },
  { id: "go_live", title: "Go Live", icon: Rocket }
];

const INDUSTRY_TEMPLATES = [
  { 
    id: "healthcare", 
    name: "Healthcare Receptionist",
    description: "HIPAA-aware, appointment scheduling, patient intake",
    greeting: "Thank you for calling [BUSINESS_NAME]. This is your AI assistant. How may I help you today?",
    icon: "🏥"
  },
  { 
    id: "legal", 
    name: "Legal Assistant",
    description: "Client intake, consultation scheduling, call screening",
    greeting: "Law offices of [BUSINESS_NAME], how may I direct your call?",
    icon: "⚖️"
  },
  { 
    id: "real_estate", 
    name: "Real Estate Agent",
    description: "Property inquiries, showing scheduling, lead qualification",
    greeting: "Thank you for calling [BUSINESS_NAME]. Are you looking to buy, sell, or rent a property?",
    icon: "🏠"
  },
  { 
    id: "restaurant", 
    name: "Restaurant Host",
    description: "Reservations, hours/menu info, waitlist management",
    greeting: "Thank you for calling [BUSINESS_NAME]. Would you like to make a reservation?",
    icon: "🍽️"
  },
  { 
    id: "automotive", 
    name: "Auto Service Advisor",
    description: "Service scheduling, parts inquiries, status updates",
    greeting: "Thank you for calling [BUSINESS_NAME]. Are you calling about sales or service?",
    icon: "🚗"
  },
  { 
    id: "general", 
    name: "General Receptionist",
    description: "Universal call handling, message taking, transfers",
    greeting: "Thank you for calling [BUSINESS_NAME]. How may I assist you today?",
    icon: "📞"
  }
];

const VOICE_OPTIONS = [
  { id: "alloy", name: "Alloy", description: "Neutral and professional" },
  { id: "echo", name: "Echo", description: "Warm and friendly" },
  { id: "nova", name: "Nova", description: "Clear and articulate" },
  { id: "shimmer", name: "Shimmer", description: "Soft and calm" }
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);
  
  const [wizardData, setWizardData] = useState({
    // Step 1: Confirm Details
    business_name: "",
    industry: "",
    phone: "",
    email: "",
    
    // Step 2: Template
    template_id: "",
    
    // Step 3: Greeting
    agent_name: "AI Assistant",
    greeting: "",
    voice_id: "alloy",
    language: "en-US",
    
    // Step 4: Phone
    phone_number: null,
    phone_type: "local",
    area_code: "",
    
    // Step 5: Test Call
    test_phone: "",
    test_completed: false,
    
    // Step 6: Go Live
    ready: false
  });

  useEffect(() => {
    // Get business ID from localStorage or URL
    const id = localStorage.getItem("business_id");
    if (id) {
      setBusinessId(id);
      fetchBusinessData(id);
    }
  }, []);

  const fetchBusinessData = async (id) => {
    try {
      const res = await axios.get(`${API}/api/v1/onboarding/status/${id}`);
      if (res.data.found) {
        setWizardData(prev => ({
          ...prev,
          ...res.data.onboarding_data
        }));
      }
    } catch (error) {
      console.error("Failed to fetch business data:", error);
    }
  };

  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;
  const currentStepData = WIZARD_STEPS[currentStep];

  const handleNext = async () => {
    // Validate current step
    if (!validateStep()) return;
    
    // Save progress
    if (businessId) {
      try {
        await axios.post(`${API}/api/v1/onboarding/wizard/${businessId}`, {
          step: currentStepData.id,
          data: wizardData
        });
      } catch (error) {
        console.error("Failed to save progress:", error);
      }
    }
    
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateStep = () => {
    switch (currentStepData.id) {
      case "confirm_details":
        if (!wizardData.business_name.trim()) {
          toast.error("Please enter your business name");
          return false;
        }
        break;
      case "choose_template":
        if (!wizardData.template_id) {
          toast.error("Please select a template");
          return false;
        }
        break;
      case "customize_greeting":
        if (!wizardData.greeting.trim()) {
          toast.error("Please enter a greeting message");
          return false;
        }
        break;
      case "test_call":
        if (!wizardData.test_completed) {
          toast.info("You can skip the test call and do it later");
        }
        break;
    }
    return true;
  };

  const handleTemplateSelect = (template) => {
    const greeting = template.greeting.replace("[BUSINESS_NAME]", wizardData.business_name || "our office");
    setWizardData(prev => ({
      ...prev,
      template_id: template.id,
      greeting
    }));
  };

  const handleGoLive = async () => {
    setLoading(true);
    try {
      // Final setup call
      if (businessId) {
        await axios.post(`${API}/api/v1/onboarding/wizard/${businessId}`, {
          step: "go_live",
          data: { ...wizardData, ready: true }
        });
      }
      
      toast.success("🎉 Your AI agent is now live!");
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      toast.error("Failed to activate agent");
    }
    setLoading(false);
  };

  const renderStepContent = () => {
    switch (currentStepData.id) {
      case "confirm_details":
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Business Name</Label>
              <Input
                value={wizardData.business_name}
                onChange={(e) => setWizardData(prev => ({ ...prev, business_name: e.target.value }))}
                placeholder="Acme Corporation"
                className="bg-slate-800 border-slate-700"
                data-testid="input-business-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Industry</Label>
              <Select 
                value={wizardData.industry} 
                onValueChange={(v) => setWizardData(prev => ({ ...prev, industry: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {INDUSTRY_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <p className="text-sm text-slate-400">
                This information helps us customize your AI agent for your specific industry and needs.
              </p>
            </div>
          </div>
        );

      case "choose_template":
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {INDUSTRY_TEMPLATES.map((template) => (
              <Card
                key={template.id}
                className={`cursor-pointer transition-all hover:border-blue-500 ${
                  wizardData.template_id === template.id 
                    ? "border-blue-500 bg-blue-500/10" 
                    : "bg-slate-800 border-slate-700"
                }`}
                onClick={() => handleTemplateSelect(template)}
                data-testid={`template-${template.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{template.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{template.name}</h4>
                        {wizardData.template_id === template.id && (
                          <Check className="w-4 h-4 text-blue-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{template.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case "customize_greeting":
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Agent Name</Label>
              <Input
                value={wizardData.agent_name}
                onChange={(e) => setWizardData(prev => ({ ...prev, agent_name: e.target.value }))}
                placeholder="AI Assistant"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Greeting Message</Label>
              <Textarea
                value={wizardData.greeting}
                onChange={(e) => setWizardData(prev => ({ ...prev, greeting: e.target.value }))}
                placeholder="Thank you for calling..."
                className="bg-slate-800 border-slate-700 min-h-[100px]"
                data-testid="greeting-textarea"
              />
              <p className="text-xs text-slate-500">
                This is what callers will hear when they first connect.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Voice</Label>
              <div className="grid grid-cols-2 gap-3">
                {VOICE_OPTIONS.map((voice) => (
                  <Button
                    key={voice.id}
                    type="button"
                    variant={wizardData.voice_id === voice.id ? "default" : "outline"}
                    className={`justify-start h-auto py-3 ${
                      wizardData.voice_id === voice.id 
                        ? "bg-blue-500" 
                        : "border-slate-700 hover:bg-slate-800"
                    }`}
                    onClick={() => setWizardData(prev => ({ ...prev, voice_id: voice.id }))}
                  >
                    <Volume2 className="w-4 h-4 mr-2" />
                    <div className="text-left">
                      <p className="font-medium">{voice.name}</p>
                      <p className="text-xs opacity-70">{voice.description}</p>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                    Preview Greeting
                  </span>
                  <Button size="sm" variant="outline" className="border-slate-600">
                    <Play className="w-4 h-4 mr-1" /> Play
                  </Button>
                </div>
                <p className="text-sm text-slate-400 italic">
                  "{wizardData.greeting || "Your greeting will appear here..."}"
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case "connect_phone":
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <Card 
                className={`cursor-pointer transition-all ${
                  wizardData.phone_type === "local" 
                    ? "border-blue-500 bg-blue-500/10" 
                    : "bg-slate-800 border-slate-700 hover:border-slate-600"
                }`}
                onClick={() => setWizardData(prev => ({ ...prev, phone_type: "local" }))}
              >
                <CardContent className="p-4 text-center">
                  <Phone className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <h4 className="font-medium">Local Number</h4>
                  <p className="text-sm text-slate-400">Choose your area code</p>
                  <p className="text-xs text-green-400 mt-2">Included in plan</p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${
                  wizardData.phone_type === "tollfree" 
                    ? "border-blue-500 bg-blue-500/10" 
                    : "bg-slate-800 border-slate-700 hover:border-slate-600"
                }`}
                onClick={() => setWizardData(prev => ({ ...prev, phone_type: "tollfree" }))}
              >
                <CardContent className="p-4 text-center">
                  <Phone className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                  <h4 className="font-medium">Toll-Free Number</h4>
                  <p className="text-sm text-slate-400">1-800/1-888 number</p>
                  <p className="text-xs text-slate-500 mt-2">+$10/mo</p>
                </CardContent>
              </Card>
            </div>
            
            {wizardData.phone_type === "local" && (
              <div className="space-y-2">
                <Label>Preferred Area Code (Optional)</Label>
                <Input
                  value={wizardData.area_code}
                  onChange={(e) => setWizardData(prev => ({ ...prev, area_code: e.target.value }))}
                  placeholder="e.g., 416, 905, 647"
                  maxLength={3}
                  className="bg-slate-800 border-slate-700 w-32"
                />
                <p className="text-xs text-slate-500">
                  We'll find an available number in your preferred area code.
                </p>
              </div>
            )}
            
            <Button className="w-full bg-blue-500 hover:bg-blue-600">
              <Phone className="w-4 h-4 mr-2" />
              Find Available Numbers
            </Button>
            
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-200">
                <strong>Note:</strong> Phone number provisioning requires active telephony configuration.
                You can skip this step and configure it later in Settings.
              </p>
            </div>
          </div>
        );

      case "test_call":
        return (
          <div className="space-y-6 text-center">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
              <PhoneCall className="w-10 h-10 text-blue-400" />
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-2">Test Your AI Agent</h3>
              <p className="text-slate-400">
                Enter your phone number and we'll call you so you can experience
                your AI agent firsthand.
              </p>
            </div>
            
            <div className="max-w-xs mx-auto space-y-4">
              <Input
                value={wizardData.test_phone}
                onChange={(e) => setWizardData(prev => ({ ...prev, test_phone: e.target.value }))}
                placeholder="+1 (555) 123-4567"
                className="bg-slate-800 border-slate-700 text-center"
                data-testid="test-phone-input"
              />
              
              <Button 
                className="w-full bg-green-500 hover:bg-green-600"
                disabled={!wizardData.test_phone}
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Call Me Now
              </Button>
            </div>
            
            {wizardData.test_completed && (
              <div className="flex items-center justify-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span>Test call completed!</span>
              </div>
            )}
            
            <Button 
              variant="ghost" 
              className="text-slate-400"
              onClick={() => setWizardData(prev => ({ ...prev, test_completed: true }))}
            >
              Skip for now →
            </Button>
          </div>
        );

      case "go_live":
        return (
          <div className="space-y-6 text-center">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <Rocket className="w-12 h-12 text-green-400" />
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-2">Ready to Go Live!</h3>
              <p className="text-slate-400">
                Your AI agent is configured and ready to handle calls.
              </p>
            </div>
            
            {/* Summary */}
            <Card className="bg-slate-800 border-slate-700 text-left">
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Agent Name</span>
                  <span className="font-medium">{wizardData.agent_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Template</span>
                  <span className="font-medium capitalize">{wizardData.template_id?.replace("_", " ")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Voice</span>
                  <span className="font-medium capitalize">{wizardData.voice_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Phone Type</span>
                  <span className="font-medium capitalize">{wizardData.phone_type}</span>
                </div>
              </CardContent>
            </Card>
            
            <Button 
              size="lg"
              className="w-full bg-green-500 hover:bg-green-600 h-14 text-lg"
              onClick={handleGoLive}
              disabled={loading}
              data-testid="go-live-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5 mr-2" />
                  Activate AI Agent
                </>
              )}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="w-8 h-8 text-blue-500" />
              <span className="text-xl font-bold">Speakr</span>
            </div>
            <div className="text-sm text-slate-400">
              Step {currentStep + 1} of {WIZARD_STEPS.length}
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="container mx-auto px-6 py-4">
        <Progress value={progress} className="h-2 bg-slate-800" />
        <div className="flex justify-between mt-2">
          {WIZARD_STEPS.map((step, idx) => {
            const StepIcon = step.icon;
            const isComplete = idx < currentStep;
            const isCurrent = idx === currentStep;
            
            return (
              <div 
                key={step.id}
                className={`flex flex-col items-center ${
                  isCurrent ? "text-blue-400" : isComplete ? "text-green-400" : "text-slate-600"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isCurrent ? "bg-blue-500/20" : isComplete ? "bg-green-500/20" : "bg-slate-800"
                }`}>
                  {isComplete ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <StepIcon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-xs mt-1 hidden md:block">{step.title}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-2xl">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {(() => {
                const Icon = currentStepData.icon;
                return <Icon className="w-6 h-6 text-blue-500" />;
              })()}
              {currentStepData.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="border-slate-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {currentStep < WIZARD_STEPS.length - 1 && (
            <Button onClick={handleNext} className="bg-blue-500 hover:bg-blue-600">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
